/**
 * Who is signed in, and what they may do.
 *
 * Permissions come from `api.my_access()` on every session, and **never** from the JWT. That is deliberate:
 * a token lives an hour, so permissions inside it would keep working for an hour after a grant is revoked.
 * Resolving them server-side per session means revocation takes effect immediately (ADR-0008).
 *
 * What this hook governs is what the user is *shown*. It is not the enforcement point and never has been:
 * every read goes through RLS and every write through an `api.*` command function, both of which check
 * again. Hiding a button the server would refuse is courtesy, not security.
 *
 * Three layers are kept apart here, because they are three different facts:
 *
 *   1. **A GoTrue session** — someone has signed in. `hasAuthSession`.
 *   2. **An ERP account** — that identity is bound to an `iam.user_accounts` row. `me.authenticated`.
 *      An invitee has (1) and not (2) until they accept.
 *   3. **Access** — the account is ACTIVE, at the assurance level it needs, and holds grants. `can()`.
 *      A suspended administrator has (1) and (2) and none of (3).
 */
import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query';
import { rpc, supabase } from './supabase';
import { announceSession, assuranceLevel, signOut as authSignOut, type AssuranceLevel } from './auth';

export interface Me {
  authenticated: boolean;
  user_account_id?: string;
  email?: string;
  status?: 'INVITED' | 'ACTIVE' | 'SUSPENDED' | 'LOCKED' | 'DEACTIVATED';
  mfa_required?: boolean;
  /** False when the account exists and is ACTIVE but may not use the ERP application (AUTH_RBAC §4). */
  erp_access?: boolean;
  /** True when this session was ended by an administrator, or by the person from another device. */
  session_revoked?: boolean;
  person?: { id: string; display_name: string; preferred_language: string } | null;
  organization?: { id: string; code: string; name: string; timezone: string; currency: string };
}

export interface MyAccess {
  permissions: { code: string; sensitivity: 'NORMAL' | 'SENSITIVE' | 'CRITICAL' }[];
  grants: { role: string; role_name: string; legal_entity_id: string | null; location_ids: string[]; valid_to: string | null }[];
  applications: { code: string; name: string }[];
}

interface SessionValue {
  /** `null` while the stored session is still being read. */
  hasAuthSession: boolean | null;
  /** The identity's email from GoTrue, available even before an ERP account exists. */
  authEmail: string | undefined;
  me: Me | undefined;
  access: MyAccess | undefined;
  aal: AssuranceLevel | undefined;
  isLoading: boolean;
  error: Error | null;
  /** True only when the permission is actually held. An unknown permission is never assumed. */
  can: (permission: string) => boolean;
  canAny: (permissions: readonly string[]) => boolean;
  /** Re-reads account, access and assurance level — after accepting an invitation or passing MFA. */
  refresh: () => Promise<void>;
  signOut: () => Promise<void>;
}

const SessionContext = createContext<SessionValue | null>(null);

export function SessionProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const [hasAuthSession, setHasAuthSession] = useState<boolean | null>(null);
  const [authEmail, setAuthEmail] = useState<string | undefined>(undefined);

  useEffect(() => {
    let cancelled = false;
    void supabase.auth.getSession().then(({ data }) => {
      if (cancelled) return;
      setHasAuthSession(Boolean(data.session));
      setAuthEmail(data.session?.user.email ?? undefined);
    });

    // Sign-in, sign-out, token refresh and MFA all arrive here. Anything the server says about the account
    // is re-read rather than inferred, because the answer may have changed underneath us.
    const { data: subscription } = supabase.auth.onAuthStateChange((_event, session) => {
      setHasAuthSession(Boolean(session));
      void announceSession(session?.access_token);
      setAuthEmail(session?.user.email ?? undefined);
      void queryClient.invalidateQueries({ queryKey: ['me'] });
      void queryClient.invalidateQueries({ queryKey: ['my_access'] });
      void queryClient.invalidateQueries({ queryKey: ['aal'] });
    });

    return () => {
      cancelled = true;
      subscription.subscription.unsubscribe();
    };
  }, [queryClient]);

  const meQuery: UseQueryResult<Me> = useQuery({
    queryKey: ['me'],
    queryFn: () => rpc<Me>('me'),
    retry: false,
    staleTime: 60_000,
    enabled: hasAuthSession === true,
  });

  // A session that was ended elsewhere is signed out here too, rather than left on a screen where every
  // panel is empty. The server has already stopped answering it; this only makes the browser agree.
  const sessionRevoked = meQuery.data?.session_revoked === true;
  useEffect(() => {
    if (sessionRevoked) void supabase.auth.signOut({ scope: 'local' });
  }, [sessionRevoked]);

  const aalQuery: UseQueryResult<AssuranceLevel> = useQuery({
    queryKey: ['aal'],
    queryFn: assuranceLevel,
    retry: false,
    staleTime: 60_000,
    enabled: hasAuthSession === true,
  });

  const accessQuery: UseQueryResult<MyAccess> = useQuery({
    queryKey: ['my_access'],
    queryFn: () => rpc<MyAccess>('my_access'),
    retry: false,
    // Short, because a revoked grant should disappear from the interface quickly too — the server has
    // already stopped honouring it.
    staleTime: 30_000,
    enabled:
      meQuery.data?.authenticated === true && meQuery.data.status === 'ACTIVE' && meQuery.data.erp_access !== false,
  });

  const value = useMemo<SessionValue>(() => {
    const held = new Set((accessQuery.data?.permissions ?? []).map((p) => p.code));
    const waitingOnAccess =
      meQuery.data?.authenticated === true && meQuery.data.status === 'ACTIVE' &&
      meQuery.data.erp_access !== false && accessQuery.isLoading;
    return {
      hasAuthSession,
      authEmail,
      me: meQuery.data,
      access: accessQuery.data,
      aal: aalQuery.data,
      isLoading:
        hasAuthSession === null || (hasAuthSession && (meQuery.isLoading || aalQuery.isLoading || waitingOnAccess)),
      error: (meQuery.error as Error | null) ?? (accessQuery.error as Error | null),
      can: (permission: string) => held.has(permission),
      canAny: (permissions: readonly string[]) => permissions.some((p) => held.has(p)),
      refresh: async () => {
        await Promise.all([
          queryClient.invalidateQueries({ queryKey: ['me'] }),
          queryClient.invalidateQueries({ queryKey: ['my_access'] }),
          queryClient.invalidateQueries({ queryKey: ['aal'] }),
        ]);
      },
      signOut: async () => {
        await authSignOut();
        queryClient.clear();
        window.location.assign('/sign-in');
      },
    };
  }, [
    hasAuthSession, authEmail, queryClient,
    meQuery.data, meQuery.isLoading, meQuery.error,
    aalQuery.data, aalQuery.isLoading,
    accessQuery.data, accessQuery.isLoading, accessQuery.error,
  ]);

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession(): SessionValue {
  const value = useContext(SessionContext);
  if (!value) throw new Error('useSession must be used inside a SessionProvider');
  return value;
}
